/**
 * TOP プリズムセクション: スクロール連動の表示制御
 * - FV: IntersectionObserver でフェードアウト
 * - ピックアップバー: コレクション先頭が画面内に入る前後で表示／非表示
 * - コレクションカード: 通常スクロール（フェード・sticky なし）
 */
(function () {
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const PICKUP_BAR_SELECTOR = '[data-top-prism-pickup-bar]';
  const COLLECTIONS_SENTINEL_SELECTOR = '[data-collections-approach-sentinel]';
  const PICKUP_HIDDEN_CLASS = 'is-hidden';
  const SPLASH_VISIBLE_DURATION_MS = 2000;
  const SPLASH_FADE_OUT_DURATION_MS = 800;
  const SPLASH_LOGO_LOAD_TIMEOUT_MS = 1000;
  const SPLASH_SCROLL_LOCK_CLASS = 'top-prism-splash-lock';
  const SPLASH_SESSION_STORAGE_KEY = 'topPrismSplashShown';
  const PRISM_END_ZONE_VIEWPORT_RATIO = 1.2;

  function debugLog(type, payload) {
    if (!window.EmissionScrollDebug) {
      return;
    }

    window.EmissionScrollDebug.log('prism:' + type, payload);
  }

  function getRoundedRect(element) {
    const rect = element.getBoundingClientRect();

    return {
      top: Math.round(rect.top),
      bottom: Math.round(rect.bottom),
      height: Math.round(rect.height),
    };
  }

  function isInPrismEndZone(sectionRoot) {
    const sectionRect = sectionRoot.getBoundingClientRect();
    const viewportHeight = Math.max(window.innerHeight, 1);

    return sectionRect.bottom <= viewportHeight * PRISM_END_ZONE_VIEWPORT_RATIO;
  }

  function dispatchPrismBackgroundVisibility(mount, paused) {
    window.dispatchEvent(
      new CustomEvent('emission:prism-background-visibility', {
        detail: {
          id: mount.id,
          paused: paused,
        },
      })
    );
  }

  /**
   * セッション単位でスプラッシュ表示済みかを判定する
   * @returns {boolean}
   */
  function hasShownSplashInSession() {
    try {
      return window.sessionStorage.getItem(SPLASH_SESSION_STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  /**
   * セッション単位でスプラッシュ表示済みフラグを保存する
   */
  function markSplashAsShownInSession() {
    try {
      window.sessionStorage.setItem(SPLASH_SESSION_STORAGE_KEY, '1');
    } catch (error) {
      // ストレージ利用不可でも表示演出自体は継続する
    }
  }

  /**
   * ヘッダーのロゴ要素を取得する（なければ null）
   * @returns {HTMLElement | null}
   */
  function findHeaderLogoElement() {
    const headerLogoSelectors = [
      '.shopify-section--header .header__logo a',
      '.shopify-section--header .header__logo',
      '.header__logo a',
      '.header__logo',
    ];

    for (const selector of headerLogoSelectors) {
      const logoElement = document.querySelector(selector);

      if (logoElement) {
        return logoElement;
      }
    }

    return null;
  }

  /**
   * ヘッダーロゴをスプラッシュ表示用として複製する
   * @param {HTMLElement} sectionRoot
   */
  function syncSplashLogoFromHeader(sectionRoot) {
    const splashLogoRoot = sectionRoot.querySelector('[data-top-prism-splash-logo]');
    const headerLogoElement = findHeaderLogoElement();

    if (!splashLogoRoot || !headerLogoElement) {
      return;
    }

    const clonedLogo = headerLogoElement.cloneNode(true);
    clonedLogo.removeAttribute('href');
    clonedLogo.setAttribute('role', 'img');
    clonedLogo.setAttribute('aria-label', 'EMISSION');
    clonedLogo.classList.add('top-prism-section__splash-logo-source');
    splashLogoRoot.innerHTML = '';
    splashLogoRoot.appendChild(clonedLogo);
  }

  /**
   * センチネル位置からピックアップバーの表示状態を同期する
   * @param {HTMLElement} bar
   * @param {DOMRectReadOnly} sentinelRect
   */
  function syncPickupBarVisibility(bar, sentinelRect) {
    const viewportHeight = window.innerHeight;

    /*
     * コレクション先頭（sentinel）が viewport 下端より下にある間（= MV 付近）だけ表示する。
     * sentinel が viewport 内・viewport より上（通過後）はどちらも非表示。
     * 以前は「viewport より上」が no-op だったため、大きなスクロールジャンプや
     * スクロール位置復元リロードで交差フレームを取りこぼすと、通過後も
     * 表示されたまま（ページ最下部で再表示に見える）になっていた。
     */
    const sentinelBelowViewport = sentinelRect.top >= viewportHeight;

    bar.classList.toggle(PICKUP_HIDDEN_CLASS, !sentinelBelowViewport);
  }

  /**
   * ピックアップバーをコレクション先頭の位置に合わせて表示／非表示する
   * @param {HTMLElement} sectionRoot
   */
  function initTopPrismPickupBar(sectionRoot) {
    const bar = sectionRoot.querySelector(PICKUP_BAR_SELECTOR);
    const collectionsSentinel = sectionRoot.querySelector(COLLECTIONS_SENTINEL_SELECTOR);

    if (sectionRoot._topPrismPickupCleanup) {
      sectionRoot._topPrismPickupCleanup();
      delete sectionRoot._topPrismPickupCleanup;
    }

    if (!bar || !collectionsSentinel) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;

    /*
     * IntersectionObserver（threshold: 0）は「交差状態が変化した瞬間」しか発火せず、
     * 慣性スクロールの大ジャンプ・End キー・スクロール位置復元リロードなどで
     * sentinel が 1 フレーム内に viewport を丸ごと跨ぐと一度も発火しない。
     * fv フェード・背景ポーズと同じ scroll + resize + rAF 方式に統一し、
     * 毎フレーム実位置から表示状態を決める（状態の取りこぼしが起きない）。
     */
    let animationFrameId = null;

    const update = () => {
      animationFrameId = null;
      syncPickupBarVisibility(bar, collectionsSentinel.getBoundingClientRect());
    };

    const scheduleUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    /* 初期状態もスクロール位置から決める（復元リロード時に MV 外なら最初から非表示） */
    syncPickupBarVisibility(bar, collectionsSentinel.getBoundingClientRect());

    sectionRoot._topPrismPickupCleanup = () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };

    if (prefersReducedMotion) {
      bar.classList.add('is-reduced-motion');
    }
  }

  /**
   * スプラッシュ内のロゴ画像が表示できる状態になるまで待つ（最長で timeoutMs）。
   * 画像が読み込み途中のまま表示してロゴが欠けて見えるのを防ぐ。
   * @param {HTMLImageElement | null} image
   * @param {number} timeoutMs
   * @returns {Promise<void>}
   */
  function waitForSplashLogoImage(image, timeoutMs) {
    return new Promise((resolve) => {
      if (!image || image.complete) {
        resolve();
        return;
      }

      let isSettled = false;

      const finalize = () => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        image.removeEventListener('load', finalize);
        image.removeEventListener('error', finalize);
        window.clearTimeout(timeoutId);
        resolve();
      };

      const timeoutId = window.setTimeout(finalize, timeoutMs);
      image.addEventListener('load', finalize);
      image.addEventListener('error', finalize);
    });
  }

  /**
   * ページアクセス時に1回だけスプラッシュを表示する
   * @param {HTMLElement} sectionRoot
   */
  function initTopPrismSplash(sectionRoot) {
    const splashRoot = sectionRoot.querySelector('[data-top-prism-splash]');

    if (!splashRoot) {
      return;
    }

    syncSplashLogoFromHeader(sectionRoot);

    if (sectionRoot._topPrismSplashCleanup) {
      sectionRoot._topPrismSplashCleanup();
      delete sectionRoot._topPrismSplashCleanup;
    }

    if (hasShownSplashInSession()) {
      // セッション内で既にスプラッシュを表示済みの場合、is-pending で背景が見えてしまうため
      // トランジションを無効化してから即座に非表示にする
      splashRoot.style.transition = 'none';
      splashRoot.classList.remove('is-pending');
      splashRoot.classList.add('is-hidden');
      splashRoot.setAttribute('aria-hidden', 'true');
      return;
    }

    markSplashAsShownInSession();

    // ロゴ読み込み完了を待つ前にスクロールをロックし、MVが操作できないようにする
    document.documentElement.classList.add(SPLASH_SCROLL_LOCK_CLASS);
    document.body.classList.add(SPLASH_SCROLL_LOCK_CLASS);

    const timers = [];
    let isCancelled = false;

    const unlockScroll = () => {
      document.documentElement.classList.remove(SPLASH_SCROLL_LOCK_CLASS);
      document.body.classList.remove(SPLASH_SCROLL_LOCK_CLASS);
    };

    /**
     * ロゴの準備が整ってから一度だけフェードインさせる。
     * 「フォールバック文言 → ロゴ画像」と二段で表示されるのを防ぐ。
     */
    const revealSplash = () => {
      if (isCancelled) {
        return;
      }

      // is-pending を外して内部コンテンツ（ロゴ・テキスト）を表示する。
      // スクロールロックは既に適用済みのためここでは追加しない。
      splashRoot.classList.remove('is-pending');
      splashRoot.classList.add('is-active');

      timers.push(
        window.setTimeout(() => {
          splashRoot.classList.add('is-concept-visible');
        }, SPLASH_VISIBLE_DURATION_MS / 2)
      );

      timers.push(
        window.setTimeout(() => {
          splashRoot.classList.add('is-leaving');
        }, SPLASH_VISIBLE_DURATION_MS)
      );

      timers.push(
        window.setTimeout(() => {
          splashRoot.classList.add('is-hidden');
          splashRoot.setAttribute('aria-hidden', 'true');
          unlockScroll();
        }, SPLASH_VISIBLE_DURATION_MS + SPLASH_FADE_OUT_DURATION_MS)
      );
    };

    const splashLogoImage = splashRoot.querySelector('img');
    waitForSplashLogoImage(splashLogoImage, SPLASH_LOGO_LOAD_TIMEOUT_MS).then(revealSplash);

    sectionRoot._topPrismSplashCleanup = () => {
      isCancelled = true;
      timers.forEach((timerId) => window.clearTimeout(timerId));
      unlockScroll();
    };
  }

  /**
   * FV を画面内に留めつつ、スクロール量に応じてフェード進行を同期する
   * @param {HTMLElement} sectionRoot
   * @param {HTMLElement} fvRoot
   */
  function initTopPrismFvScrollFade(sectionRoot, fvRoot) {
    if (sectionRoot._topPrismFvFadeCleanup) {
      sectionRoot._topPrismFvFadeCleanup();
      delete sectionRoot._topPrismFvFadeCleanup;
    }

    const prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;

    if (prefersReducedMotion) {
      sectionRoot.classList.add('is-fv-faded');
      sectionRoot.style.removeProperty('--top-prism-fv-fade-progress');
      return;
    }

    let animationFrameId = null;
    let lastFvDebugTime = 0;
    let isFvLockedComplete = false;

    const updateFadeProgress = () => {
      const updateStart = performance.now();
      animationFrameId = null;
      const sectionRect = sectionRoot.getBoundingClientRect();
      const viewportHeight = Math.max(window.innerHeight, 1);

      if (sectionRect.bottom <= viewportHeight * PRISM_END_ZONE_VIEWPORT_RATIO) {
        if (sectionRoot.style.getPropertyValue('--top-prism-fv-fade-progress') !== '1') {
          sectionRoot.style.setProperty('--top-prism-fv-fade-progress', '1');
        }

        if (!sectionRoot.classList.contains('is-fv-faded')) {
          sectionRoot.classList.add('is-fv-faded');
        }

        const now = performance.now();

        if (now - lastFvDebugTime > 500) {
          lastFvDebugTime = now;
          debugLog('fv:skip', {
            reason: 'end-zone',
            rect: {
              top: Math.round(sectionRect.top),
              bottom: Math.round(sectionRect.bottom),
              height: Math.round(sectionRect.height),
            },
          });
        }

        return;
      }

      if (sectionRect.top <= -viewportHeight) {
        if (sectionRoot.style.getPropertyValue('--top-prism-fv-fade-progress') !== '1') {
          sectionRoot.style.setProperty('--top-prism-fv-fade-progress', '1');
        }

        if (!sectionRoot.classList.contains('is-fv-faded')) {
          sectionRoot.classList.add('is-fv-faded');
        }

        if (!isFvLockedComplete) {
          isFvLockedComplete = true;
          debugLog('fv:skip', {
            reason: 'faded',
            rect: {
              top: Math.round(sectionRect.top),
              bottom: Math.round(sectionRect.bottom),
              height: Math.round(sectionRect.height),
            },
          });
        }

        return;
      }

      isFvLockedComplete = false;

      if (sectionRect.bottom < 0) {
        if (sectionRoot.style.getPropertyValue('--top-prism-fv-fade-progress') !== '1') {
          sectionRoot.style.setProperty('--top-prism-fv-fade-progress', '1');
        }

        if (!sectionRoot.classList.contains('is-fv-faded')) {
          sectionRoot.classList.add('is-fv-faded');
        }

        return;
      }

      // スクロール量を 0〜1 の線形値に変換する
      const t = Math.max(0, Math.min(1, -sectionRect.top / viewportHeight));
      // ease-out cubic を適用する: 定位置に近づくほど動きが遅くなり、終わりがゆっくりになる
      const fadeProgress = 1 - Math.pow(1 - t, 3);

      sectionRoot.style.setProperty('--top-prism-fv-fade-progress', fadeProgress.toFixed(4));

      if (fadeProgress >= 1) {
        sectionRoot.classList.add('is-fv-faded');
      } else {
        sectionRoot.classList.remove('is-fv-faded');
      }

      const now = performance.now();
      const updateDuration = now - updateStart;

      if (updateDuration > 4 || now - lastFvDebugTime > 500) {
        lastFvDebugTime = now;
        debugLog('fv:update', {
          duration: Math.round(updateDuration * 10) / 10,
          fadeProgress: Math.round(fadeProgress * 1000) / 1000,
          rect: {
            top: Math.round(sectionRect.top),
            bottom: Math.round(sectionRect.bottom),
            height: Math.round(sectionRect.height),
          },
        });
      }
    };

    const scheduleUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(updateFadeProgress);
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    scheduleUpdate();

    sectionRoot._topPrismFvFadeCleanup = () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      sectionRoot.style.removeProperty('--top-prism-fv-fade-progress');
    };
  }

  /**
   * New Arrivals に入る直前で Prism WebGL の描画ループを止める。
   * sticky 背景が親セクションの終端で外れる瞬間に WebGL rAF と重なると、
   * Lenis のスクロールフレームが飛ぶことがあるため、見え方を保ったまま少し早めに停止する。
   * @param {HTMLElement} sectionRoot
   */
  function initTopPrismBackgroundPause(sectionRoot) {
    if (sectionRoot._topPrismBackgroundPauseCleanup) {
      sectionRoot._topPrismBackgroundPauseCleanup();
      delete sectionRoot._topPrismBackgroundPauseCleanup;
    }

    const prismMount = sectionRoot.querySelector('[data-prism-react-background] .bg-animation__mount');

    if (!prismMount) {
      return;
    }

    let animationFrameId = null;
    let isPaused = false;

    const setPaused = (paused) => {
      if (paused === isPaused) {
        return;
      }

      isPaused = paused;
      dispatchPrismBackgroundVisibility(prismMount, isPaused);
      debugLog('bg:' + (isPaused ? 'pause' : 'resume'), {
        rect: getRoundedRect(sectionRoot),
      });
    };

    const updatePauseState = () => {
      animationFrameId = null;
      setPaused(isInPrismEndZone(sectionRoot));
    };

    const scheduleUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(updatePauseState);
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    scheduleUpdate();

    sectionRoot._topPrismBackgroundPauseCleanup = () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      setPaused(false);
    };
  }

  /**
   * ニュースバーの非表示NEWSを読み上げ・Tab対象から外す
   * @param {HTMLElement} carousel
   */
  function syncNewsCarouselA11y(carousel) {
    Array.from(carousel.children).forEach((item) => {
      const selected = item.classList.contains('is-selected');

      item.toggleAttribute('inert', !selected);
      item.setAttribute('aria-hidden', selected ? 'false' : 'true');

      if (item.tagName === 'A') {
        if (selected) {
          item.removeAttribute('tabindex');
        } else {
          item.setAttribute('tabindex', '-1');
        }
      }
    });
  }

  /**
   * ニュースバーのアクセシビリティをカルーセル状態に同期する
   * @param {HTMLElement} sectionRoot
   */
  function initTopPrismNewsCarousel(sectionRoot) {
    const carousel = sectionRoot.querySelector('[data-top-prism-news-carousel]');

    if (sectionRoot._topPrismNewsCleanup) {
      sectionRoot._topPrismNewsCleanup();
      delete sectionRoot._topPrismNewsCleanup;
    }

    if (!carousel) {
      return;
    }

    const onChange = () => syncNewsCarouselA11y(carousel);

    /*
     * PC のバー幅を「表示中タイトル」のコンテンツ幅に同期する。
     * CSS 側（top-prism-section.css）で非選択スライドは absolute のため、
     * 明示 width が無い状態のレイアウト幅 = 選択中スライドの必要幅になる。
     * 切替（carousel:change）では次スライドの幅へ transition（0.25s）で移行し、
     * フェードアウトと同時に幅が変わる。SP / タブレットでは何もしない。
     */
    const newsPcMedia = window.matchMedia('(min-width: 1025px)');

    const measureCellWidth = (cell) => {
      if (!cell) {
        return 0;
      }
      return Math.ceil(cell.getBoundingClientRect().width);
    };

    const onWidthChange = (event) => {
      if (!newsPcMedia.matches) {
        return;
      }
      const target = measureCellWidth(event.detail && event.detail.cell);
      if (target > 0) {
        carousel.style.width = target + 'px';
      }
    };

    const applyNewsCarouselWidth = () => {
      if (!newsPcMedia.matches) {
        carousel.style.removeProperty('width');
        return;
      }
      /* 同期処理内で width を外して実幅を測り直す（再描画は挟まないためチラつかない）
         ClientFB_39: CSS の transition: width が効いたまま width を外して
         直後に同期計測すると、Chromium では遷移「開始値」（= 旧 width）が
         返り、古い幅をそのまま再固定してしまう（resize 後に幅が追従しない
         実測原因）。計測の間だけ transition を無効化し、値を確定させてから
         インライン指定を外して CSS の 0.25s に戻す。最終 width は計測値
         そのものなので見た目のジャンプは起きない */
      carousel.style.transition = 'none';
      carousel.style.removeProperty('width');
      const selected = carousel.querySelector('.is-selected');
      const target = measureCellWidth(selected);
      if (target > 0) {
        carousel.style.width = target + 'px';
      }
      void carousel.offsetWidth;
      carousel.style.removeProperty('transition');
    };

    let newsWidthResizeRaf = null;
    const onWidthResize = () => {
      if (newsWidthResizeRaf !== null) {
        return;
      }
      newsWidthResizeRaf = window.requestAnimationFrame(() => {
        newsWidthResizeRaf = null;
        applyNewsCarouselWidth();
      });
    };

    carousel.addEventListener('carousel:change', onChange);
    carousel.addEventListener('carousel:settle', onChange);
    carousel.addEventListener('carousel:change', onWidthChange);
    carousel.addEventListener('carousel:settle', applyNewsCarouselWidth);
    window.addEventListener('resize', onWidthResize);
    newsPcMedia.addEventListener('change', applyNewsCarouselWidth);
    syncNewsCarouselA11y(carousel);
    applyNewsCarouselWidth();

    /* Web フォント適用後に実幅が変わるため測り直す */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(applyNewsCarouselWidth).catch(() => {});
    }

    /*
     * ClientFB_39: 初回の幅計測は Adobe Fonts 適用前（html.wf-loading =
     * フォールバックフォント）に走るため、適用後の実テキスト幅と食い違った px が
     * style.width に固定され続ける（実測: 同一タイトルでフォールバック計測 358px /
     * Adobe 計測 329px。Safari / WebKit はこの幅をそのまま描画するため、
     * 差がマイナスなら「…」・プラスなら右側の空白として恒在化していた）。
     * 上の document.fonts.ready は Typekit のフォント読み込み開始前
     * （Typekit.load() は parse 完了後）に解決し得るため当てにできない。
     * 既存 FOUT 機構が html に付与する wf-active / wf-inactive のクラス変化を
     * 監視し、フォント表示状態が確定・変化したタイミングで既存の
     * applyNewsCarouselWidth() を再実行する（計測ロジック自体は再利用。
     * Typekit / FOUT / scriptTimeout 側は一切変更しない）。
     * scriptTimeout 後にフォントが遅れて届く wf-inactive → wf-active の
     * 再切替でも state が変わるため、もう一度測り直される。
     */
    let fontStateSeen = '';
    let fontRemeasureRaf = null;
    const remeasureAfterFonts = () => {
      if (fontRemeasureRaf !== null) {
        window.cancelAnimationFrame(fontRemeasureRaf);
      }
      /* フォント差し替え直後はレイアウト反映前のことがあるため 2 フレーム待つ */
      fontRemeasureRaf = window.requestAnimationFrame(() => {
        fontRemeasureRaf = window.requestAnimationFrame(() => {
          fontRemeasureRaf = null;
          applyNewsCarouselWidth();
        });
      });
    };
    const onFontStateChange = () => {
      const htmlClass = document.documentElement.className;
      let state = '';
      if (htmlClass.indexOf('wf-active') !== -1) {
        state = 'active';
      } else if (htmlClass.indexOf('wf-inactive') !== -1) {
        state = 'inactive';
      }
      if (state !== '' && state !== fontStateSeen) {
        fontStateSeen = state;
        remeasureAfterFonts();
      }
    };
    const fontStateObserver = new MutationObserver(onFontStateChange);
    fontStateObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    /* observer 登録時点で既にフォント状態が確定している場合も 1 回測り直す */
    onFontStateChange();

    sectionRoot._topPrismNewsCleanup = () => {
      carousel.removeEventListener('carousel:change', onChange);
      carousel.removeEventListener('carousel:settle', onChange);
      carousel.removeEventListener('carousel:change', onWidthChange);
      carousel.removeEventListener('carousel:settle', applyNewsCarouselWidth);
      window.removeEventListener('resize', onWidthResize);
      newsPcMedia.removeEventListener('change', applyNewsCarouselWidth);
      if (newsWidthResizeRaf !== null) {
        window.cancelAnimationFrame(newsWidthResizeRaf);
        newsWidthResizeRaf = null;
      }
      fontStateObserver.disconnect();
      if (fontRemeasureRaf !== null) {
        window.cancelAnimationFrame(fontRemeasureRaf);
        fontRemeasureRaf = null;
      }
      carousel.style.removeProperty('width');
    };
  }

  /**
   * 1セクション分のスクロール演出を初期化する
   * @param {HTMLElement} sectionRoot
   */
  function initTopPrismSection(sectionRoot) {
    initTopPrismSplash(sectionRoot);
    initTopPrismPickupBar(sectionRoot);
    initTopPrismNewsCarousel(sectionRoot);
    initTopPrismBackgroundPause(sectionRoot);

    const fvRoot = sectionRoot.querySelector('[data-fv-root]');
    sectionRoot.classList.add('is-fv-transition-ready');

    if (!fvRoot) {
      sectionRoot.classList.add('is-fv-faded');
      return;
    }
    initTopPrismFvScrollFade(sectionRoot, fvRoot);
  }

  function initAll() {
    document.querySelectorAll('[data-top-prism-section]').forEach(initTopPrismSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target.querySelector('[data-top-prism-section]');

    if (sectionRoot) {
      initTopPrismSection(sectionRoot);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionRoot = event.target.querySelector('[data-top-prism-section]');

    if (!sectionRoot) {
      return;
    }

    if (sectionRoot._topPrismPickupCleanup) {
      sectionRoot._topPrismPickupCleanup();
      delete sectionRoot._topPrismPickupCleanup;
    }

    if (sectionRoot._topPrismSplashCleanup) {
      sectionRoot._topPrismSplashCleanup();
      delete sectionRoot._topPrismSplashCleanup;
    }

    if (sectionRoot._topPrismFvFadeCleanup) {
      sectionRoot._topPrismFvFadeCleanup();
      delete sectionRoot._topPrismFvFadeCleanup;
    }

    if (sectionRoot._topPrismBackgroundPauseCleanup) {
      sectionRoot._topPrismBackgroundPauseCleanup();
      delete sectionRoot._topPrismBackgroundPauseCleanup;
    }

    if (sectionRoot._topPrismNewsCleanup) {
      sectionRoot._topPrismNewsCleanup();
      delete sectionRoot._topPrismNewsCleanup;
    }
  });
})();
